import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { boringBucketService, FounderConnection } from '../../services/boringBucketService';
import '../Dashboard.css';

/**
 * Founder Console — founder-only configuration for The Boring Bucket.
 * v1: pick which connected TastyTrade account feeds Founder's Trades.
 * Connections come from the central Profile; this only selects one.
 */
export const FounderConsole: React.FC = () => {
  const { token } = useAuth();
  const [state, setState] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [connections, setConnections] = useState<FounderConnection[]>([]);
  const [currentAccount, setCurrentAccount] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    boringBucketService.getFounderConfig(token)
      .then((d) => {
        setConnections(d.connections || []);
        setCurrentAccount(d.current?.accountNumber || null);
        const cur = (d.connections || []).find((c) => c.accountNumber === d.current?.accountNumber);
        setSelected(cur?.connectionId || '');
        setState('ready');
      })
      .catch((err) => setState(err?.response?.status === 403 ? 'forbidden' : 'error'));
  }, [token]);

  const save = async () => {
    if (!token || !selected) return;
    setSaving(true);
    setMsg(null);
    try {
      const d = await boringBucketService.setFounderAccount(token, selected);
      setCurrentAccount(d.current?.accountNumber || null);
      setMsg('Saved. The next sync will re-baseline and switch Founder’s Trades to this account.');
    } catch {
      setMsg('Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">
        <div style={{ padding: '1.5rem', maxWidth: 680 }}>
          <div className="welcome-section">
            <h2>Founder Console</h2>
            <p>Founder-only configuration for The Boring Bucket.</p>
            <Link to="/boring-bucket" style={{ fontSize: 13, color: '#60a5fa' }}>&larr; Boring Bucket</Link>
          </div>

          {state === 'loading' && <p style={{ color: '#94a3b8' }}>Loading…</p>}

          {state === 'forbidden' && (
            <div style={panel}>
              <h3 style={h3}>Founder access only</h3>
              <p style={p}>This area is limited to The Boring Bucket founder.</p>
            </div>
          )}

          {state === 'error' && (
            <div style={panel}>
              <h3 style={h3}>Couldn't load</h3>
              <p style={p}>Something went wrong loading the console. Refresh to try again.</p>
            </div>
          )}

          {state === 'ready' && (
            <div style={panel}>
              <h3 style={h3}>Founder's Trades — source account</h3>
              <p style={p}>
                The connected account members watch on the Founder's Trades page. Run the
                program on a representative account here; your other accounts stay private.
                Connect accounts in your Profile — this only selects which one is the source.
              </p>

              {connections.length === 0 ? (
                <p style={{ ...p, color: '#fbbf24' }}>
                  No TastyTrade accounts connected. Add one in your{' '}
                  <Link to="/profile" style={{ color: '#60a5fa' }}>Profile</Link> first.
                </p>
              ) : (
                <>
                  <div style={{ margin: '14px 0' }}>
                    {connections.map((c) => {
                      const isCurrent = c.accountNumber === currentAccount;
                      return (
                        <label key={c.connectionId} style={{
                          display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                          padding: '12px 14px', marginBottom: 8, borderRadius: 8,
                          background: selected === c.connectionId ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${selected === c.connectionId ? '#10b981' : 'rgba(255,255,255,0.12)'}`,
                        }}>
                          <input
                            type="radio"
                            name="founder-account"
                            checked={selected === c.connectionId}
                            onChange={() => setSelected(c.connectionId)}
                          />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ color: '#f9fafb', fontSize: 14, fontWeight: 600 }}>{c.label}</span>
                            <span style={{ color: '#94a3b8', fontSize: 13 }}>
                              {'  '}· {c.accountNumber}{c.accountType ? ` · ${c.accountType}` : ''}
                            </span>
                            {isCurrent && (
                              <span style={{ color: '#10b981', fontSize: 12, fontWeight: 600 }}>{'  '}· current</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {msg && <p style={{ ...p, color: msg.startsWith('Saved') ? '#10b981' : '#f87171' }}>{msg}</p>}

                  <button
                    onClick={save}
                    disabled={saving || !selected || connections.find((c) => c.connectionId === selected)?.accountNumber === currentAccount}
                    style={{
                      marginTop: 4, padding: '10px 24px', borderRadius: 8, border: 'none',
                      fontWeight: 700, fontSize: 14,
                      background: saving ? '#475569' : '#10b981', color: '#0a0f1e',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: (!selected || connections.find((c) => c.connectionId === selected)?.accountNumber === currentAccount) ? 0.5 : 1,
                    }}
                  >
                    {saving ? 'Saving…' : 'Set as Founder’s Trades account'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const panel: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10, padding: '22px 24px',
};
const h3: React.CSSProperties = { margin: '0 0 8px', color: '#f9fafb', fontSize: '1.1em' };
const p: React.CSSProperties = { color: '#94a3b8', fontSize: 14, lineHeight: 1.7, margin: '0 0 8px' };

export default FounderConsole;
